import React, { type ReactNode, Suspense, useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import UFOSegment from '@atlaskit/react-ufo/segment';

import {
	EditionAwarenessContext,
	type EditionAwarenessContextType,
} from '../services/edition-awareness-context';
import { getChargeElementKey } from '../services/get-charge-element-key';
import { getJiraApplicationKey } from '../services/get-jira-application-key';
import { getTrialDaysLeft } from '../services/get-trial-days-left';
import { getTrialOfferingName } from '../services/get-trial-offering-name';
import { getHamsKey } from '../services/product-to-hams-key';
import { useConfig } from '../services/use-config';
import { useSpotlight } from '../services/use-spotlight';
import type { Edition, Product, SiteType, UserRole } from '../types';

import type { editionAwarenessQuery } from './__generated__/editionAwarenessQuery.graphql';
import { AudienceAndTiming } from './audience-and-timing';
import { ErrorBoundary } from './error-boundary';
import { Spotlight } from './spotlight';

function getBillingSourceSystem(typename?: string | null) {
	switch (typename) {
		case 'CcpEntitlement':
			return 'CCP';
		case 'HamsEntitlement':
			return 'HAMS';
	}
}

export type EditionAwarenessProps = {
	cloudId: string;
	product: Product;
	edition: Edition;
	freeButton: ReactNode;
	trialButton: ReactNode;
	siteType?: SiteType;
	userRoles?: UserRole[];
	hasDismissedButton?: boolean;
	onDismissButton?: () => void;
	isInReverseTrial?: boolean;
	onOpenOnboardingMenu?: () => void;
	shouldAutoOpen?: boolean;
	onAutoOpen?: () => void;
};

/**
 * Edition awareness is used to communicate to customers about the current edition they have and if they should
 * - Add payment details
 * - Upgrade their edition
 *
 * This component deals with creating the context which is then used throughout the package to:
 * - Track analytics
 * - Know which product/edition/cloud id is being used
 * - Know when to show/hide the button
 */
function EditionAwarenessImpl({ freeButton, trialButton, ...props }: EditionAwarenessProps) {
	const {
		edition,
		product,
		cloudId,
		userRoles: userRolesFromProduct,
		hasDismissedButton,
		onDismissButton,
		siteType,
		isInReverseTrial,
		onOpenOnboardingMenu,
		shouldAutoOpen,
		onAutoOpen,
	} = props;

	const config = useConfig(product, edition);
	const { isSpotlightActive, handleSetSpotlight } = useSpotlight();

	const trialOfferingName = getTrialOfferingName(edition, config);

	const data = useLazyLoadQuery<editionAwarenessQuery>(
		graphql`
			query editionAwarenessQuery(
				$cloudId: ID!
				$productKey: String!
				$trialOfferingName: String!
				$shouldCheckForChangeOffering: Boolean!
				$shouldCheckForTrial: Boolean!
				$shouldSkip: Boolean!
				$shouldCheckForChargeQuantities: Boolean!
				$entitlementFilter: CommerceEntitlementFilter
				$chargeElement: String!
				$shouldCheckBillingAdmin: Boolean!
				$shouldCheckForTwc: Boolean!
				$shouldCheckForStorageUsageJira: Boolean!
				$applicationKey: JiraApplicationKey!
				$shouldCheckForStorageUsageConfluence: Boolean!
			) {
				...audienceAndTiming_AtlassianEditionAwareness

				tenantContexts(cloudIds: [$cloudId]) @skip(if: $shouldSkip) {
					entitlementInfo(hamsProductKey: $productKey) {
						entitlement(where: $entitlementFilter) {
							__typename
							experienceCapabilities @include(if: $shouldCheckForChangeOffering) {
								changeOfferingV2(offeringName: $trialOfferingName) {
									isAvailableToUser
									experienceUrl
								}
							}

							preDunning @include(if: $shouldCheckForTrial) {
								status
							}

							subscription @include(if: $shouldCheckForTrial) {
								trial {
									timeLeft
								}
								accountDetails {
									invoiceGroup {
										invoiceable
										experienceCapabilities {
											configurePaymentV2 {
												experienceUrl
												isAvailableToUser
											}
										}
									}
								}
							}

							offering @include(if: $shouldCheckForTrial) {
								trial {
									lengthDays
								}
							}

							transactionAccount @include(if: $shouldCheckBillingAdmin) {
								isCurrentUserBillingAdmin
							}

							relatesFromEntitlements @include(if: $shouldCheckForTwc) {
								relationshipType
								entitlementId
							}
						}
					}
				}
			}
		`,
		{
			shouldSkip: config?.shouldSkip === true,
			trialOfferingName: trialOfferingName,
			shouldCheckForChangeOffering: edition === 'free',
			shouldCheckForTrial: edition === 'premium' || edition === 'standard',
			cloudId,
			productKey: getHamsKey(product),
			shouldCheckForChargeQuantities:
				config.warnOnUserLimit === true ||
				config.isStandardTrialsUserAndStorageLimitBreakersEnabled === true ||
				// Confluence doesn't use `warnOnUserLimit` and has its own variety
				(product === 'confluence' && edition === 'free'),
			// The entitlement filter ensures that we apply the `inTrialOrPreDunning` only when an entitlement is
			// on a non free edition. The filter is based on the trial end timestamp inside of TCS, this means that
			// if a customer downgrades their product to free while on a trial, then they will only show up
			// if inTrialOrPreDunning is set to true as the filter assumes they are still on trial.
			entitlementFilter: edition !== 'free' ? { inTrialOrPreDunning: true } : null,
			chargeElement: getChargeElementKey(product),
			shouldCheckBillingAdmin: config.shouldCheckBillingAdmin === true,
			shouldCheckForTwc: config.shouldCheckForTwc === true,
			shouldCheckForStorageUsageJira:
				product === 'jira' && config.isStandardTrialsUserAndStorageLimitBreakersEnabled === true,
			applicationKey: getJiraApplicationKey(product) ?? 'JIRA_SOFTWARE',
			shouldCheckForStorageUsageConfluence:
				product === 'confluence' &&
				config.isStandardTrialsUserAndStorageLimitBreakersEnabled === true,
		},
	);

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;

	const isInvoiceable = entitlement?.subscription?.accountDetails?.invoiceGroup?.invoiceable;

	const canAddPaymentDetails =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePaymentV2?.isAvailableToUser === true;
	const addPaymentDetailsExperienceUrl =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePaymentV2?.experienceUrl;

	const trialDaysLeft = getTrialDaysLeft(entitlement?.subscription?.trial?.timeLeft);

	const trialLengthDays = entitlement?.offering?.trial?.lengthDays ?? undefined;

	const isInTrial = Boolean(trialDaysLeft && trialDaysLeft > 0);

	const isInPredunning = entitlement?.preDunning?.status === 'IN_PRE_DUNNING' && !isInvoiceable;

	const isChangeOfferingAvailable =
		entitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser;
	const changeOfferingExperienceUrl =
		entitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl;

	const canUpgrade = Boolean(isChangeOfferingAvailable && changeOfferingExperienceUrl);

	const billingSourceSystem = getBillingSourceSystem(entitlement?.__typename);

	const isInTeamWorkCollection = useMemo(() => {
		const collectionEntitlement = entitlement?.relatesFromEntitlements?.find(
			(item) => item?.relationshipType === 'COLLECTION',
		);

		return !!collectionEntitlement?.entitlementId;
	}, [entitlement?.relatesFromEntitlements]);

	const userRoles: UserRole[] = useMemo(() => {
		const roles: UserRole[] = [];
		if (userRolesFromProduct?.includes('SITE_ADMIN')) {
			roles.push('SITE_ADMIN');
		}
		if (
			data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.transactionAccount
				?.isCurrentUserBillingAdmin
		) {
			roles.push('BILLING_ADMIN');
		}
		return roles;
	}, [data?.tenantContexts, userRolesFromProduct]);

	const context: EditionAwarenessContextType = useMemo(() => {
		return {
			cloudId,
			product,
			edition,
			isInvoiceable,
			canAddPaymentDetails,
			addPaymentDetailsExperienceUrl,
			trialDaysLeft,
			trialLengthDays,
			isInTrial,
			isInPredunning,
			isInReverseTrial,
			canUpgrade,
			isInTeamWorkCollection,
			config,
			userRoles,
			hasDismissedButton,
			onDismissButton,
			siteType,
			isSpotlightActive,
			handleSetSpotlight,
			billingSourceSystem,
			onOpenOnboardingMenu,
			shouldAutoOpen,
			onAutoOpen,
		};
	}, [
		cloudId,
		product,
		edition,
		isInvoiceable,
		canAddPaymentDetails,
		addPaymentDetailsExperienceUrl,
		trialDaysLeft,
		trialLengthDays,
		isInTrial,
		isInPredunning,
		isInReverseTrial,
		canUpgrade,
		isInTeamWorkCollection,
		config,
		userRoles,
		hasDismissedButton,
		onDismissButton,
		siteType,
		isSpotlightActive,
		handleSetSpotlight,
		billingSourceSystem,
		onOpenOnboardingMenu,
		shouldAutoOpen,
		onAutoOpen,
	]);

	return (
		<EditionAwarenessContext.Provider value={context}>
			<ErrorBoundary product={product} edition={edition} id="audience-and-timing">
				<AudienceAndTiming freeButton={freeButton} trialButton={trialButton} entitlement={data} />
				<Spotlight />
			</ErrorBoundary>
		</EditionAwarenessContext.Provider>
	);
}

export function EditionAwareness(props: EditionAwarenessProps) {
	const { product, edition } = props;

	return (
		<UFOSegment name="edition-awareness-platform">
			{/* This needs to be something that is not null, if it is null then the error will
				bubble up to the parent as `ErrorBoundary` will throw an exception if it is passed `null` */}
			<ErrorBoundary product={product} edition={edition} id="edition-awareness">
				<Suspense fallback={null}>
					<EditionAwarenessImpl {...props} />
				</Suspense>
			</ErrorBoundary>
		</UFOSegment>
	);
}
